/**
 * ServerModals.jsx – Add Server, Delete Logs, Truncate Logs modals
 */
import React, { useState, useEffect } from 'react';
import { FiX, FiTrash2, FiCalendar, FiServer, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { fetchServerNames, addServer, deleteServerLogs, truncateServerLogs } from './serverApi';

/* ─── Shared checkbox list ─────────────────────────────────────────────────── */
function ServerCheckList({ servers, selected, onChange }) {
  return (
    <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
      {servers.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-4">No servers found</p>
      )}
      {servers.map((s) => (
        <label key={s} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
          <input
            type="checkbox"
            checked={selected.includes(s)}
            onChange={(e) =>
              onChange(e.target.checked ? [...selected, s] : selected.filter((x) => x !== s))
            }
            className="rounded border-gray-300 text-red-700 focus:ring-red-600"
          />
          {s}
        </label>
      ))}
    </div>
  );
}

/* ─── Modal shell ──────────────────────────────────────────────────────────── */
function Modal({ open, onClose, headerColor, title, icon: Icon, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95">
        <div className={`${headerColor} text-white p-4 flex items-center justify-between`}>
          <h3 className="font-bold flex items-center gap-2"><Icon className="w-4 h-4" />{title}</h3>
          <button onClick={onClose} className="hover:opacity-70 transition"><FiX /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ─── Add Server Modal ─────────────────────────────────────────────────────── */
export function AddServerModal({ open, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const res = await addServer(name.trim());
      if (res.data?.status) {
        toast.success(res.data.msg || 'Server Added');
        setName('');
        onClose();
        onSuccess?.();
      } else {
        toast.error(res.data?.msg || 'Failed to add server');
      }
    } catch {
      toast.error('Request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} headerColor="bg-red-700" title="Add New Server Node" icon={FiServer}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Server Hostname / Name</label>
          <input
            type="text" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. ubuntu-master-01"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 text-sm text-white bg-red-700 hover:bg-red-800 rounded-lg font-semibold transition">
            {loading ? 'Saving…' : 'Add Node'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Delete Server Logs Modal ─────────────────────────────────────────────── */
export function DeleteServerModal({ open, onClose, onSuccess }) {
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    fetchServerNames()
      .then((res) => {
        if (res.data?.data) {
          setServers([...new Set(res.data.data.map((s) => s.server_name).filter(Boolean))]);
        }
      })
      .catch(() => {});
  }, [open]);

  const handleInitialSubmit = () => {
    if (selected.length === 0) { toast.warning('Select at least one server'); return; }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      await deleteServerLogs(selected);
      toast.success('Log records removed.');
      onClose(); onSuccess?.();
    } catch { toast.error('Failed to delete logs'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} headerColor="bg-red-600" title="Delete Server Status Logs" icon={FiTrash2}>
      <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Select servers to clear all logs:</p>
      <ServerCheckList servers={servers} selected={selected} onChange={setSelected} />
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold">Cancel</button>
        <button onClick={handleInitialSubmit} disabled={loading} className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
          {loading ? 'Processing…' : 'Delete Logs'}
        </button>
      </div>

      <ConfirmDialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)} 
        onConfirm={handleConfirmDelete} 
        title="Delete Logs?" 
        text={`This permanently removes ALL status logs for: ${selected.join(', ')}`} 
        confirmText="Yes, Delete!" 
        confirmColor="bg-red-600 hover:bg-red-700" 
        icon={FiAlertTriangle}
      />
    </Modal>
  );
}

/* ─── Truncate Server Logs Modal ───────────────────────────────────────────── */
export function TruncateLogModal({ open, onClose, onSuccess }) {
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [cutoffDate, setCutoffDate] = useState('');
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setCutoffDate('');
    fetchServerNames()
      .then((res) => {
        if (res.data?.data)
          setServers([...new Set(res.data.data.map((s) => s.server_name).filter(Boolean))]);
      })
      .catch(() => {});
  }, [open]);

  const handleInitialSubmit = () => {
    if (!selected.length) { toast.warning('Select at least one server'); return; }
    if (!cutoffDate) { toast.warning('Select a cut-off date'); return; }
    setConfirmOpen(true);
  };

  const handleConfirmTruncate = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      await truncateServerLogs(selected, cutoffDate);
      toast.success('Logs truncated successfully.');
      onClose(); onSuccess?.();
    } catch { toast.error('Failed to truncate logs'); }
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} headerColor="bg-red-600" title="Truncate Server Logs" icon={FiCalendar}>
      <p className="text-xs text-gray-500 uppercase font-semibold mb-2">1. Select servers:</p>
      <ServerCheckList servers={servers} selected={selected} onChange={setSelected} />
      <p className="text-xs text-gray-500 uppercase font-semibold mt-4 mb-2">2. Delete logs older than:</p>
      <input type="date" value={cutoffDate} onChange={(e) => setCutoffDate(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600" />
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold">Cancel</button>
        <button onClick={handleInitialSubmit} disabled={loading} className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
          {loading ? 'Processing…' : 'Truncate Logs'}
        </button>
      </div>

      <ConfirmDialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)} 
        onConfirm={handleConfirmTruncate} 
        title="Truncate Logs?" 
        text={`Delete logs older than ${cutoffDate} for: ${selected.join(', ')}`} 
        confirmText="Yes, Truncate!" 
        confirmColor="bg-red-600 hover:bg-red-700" 
        icon={FiAlertCircle}
      />
    </Modal>
  );
}

/* ─── Generic Confirm Dialog ────────────────────────────────────────────────── */
export function ConfirmDialog({ open, onClose, onConfirm, title, text, confirmText = 'Confirm', confirmColor = 'bg-red-600 hover:bg-red-700', icon: Icon = FiAlertTriangle, loading = false }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} headerColor={confirmColor.split(' ')[0]} title={title} icon={Icon}>
      <p className="text-sm text-gray-600 mb-6">{text}</p>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold transition">Cancel</button>
        <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm text-white rounded-lg font-semibold transition ${confirmColor}`}>
          {loading ? 'Processing…' : confirmText}
        </button>
      </div>
    </Modal>
  );
}
